import axios from 'axios';
import {Pref} from '@/domain/pref.entity';
import {City} from '@/domain/city.entity';

export interface EstateCount {
  shopId: number,
  encodedShopId?: string,
  shopName: string,
  fullAddress: string,
  total: number,
  prefId: number,
  prefName: string,
  address: string,
  cityId: number,
  cityName: string,
  landCount: number,
  newEstateCount: number,
  newMansionCount: number,
  oldEstateCount: number,
  oldMansionCount: number,
  businessCount: number,
}

export interface GetEstateCountForm {
  shopIds?: string[] | string;
  prefId?: string | number,
  cityIds?: number[],
  townId?: string | number,
  zipCode?: string,
  latitude?: number,
  longitude?: number,
}

export interface GetFcShopHouseDoDetailForm {
    fcShopId: number
}

export interface WorkTime {
    open: `${number}:${number}:${number}` | null,
    close: `${number}:${number}:${number}` | null,
}

export interface FcShopHouseDoDetail {
    id: number
    shopName: string
    url: string
    name: string
    freeDial: string
    tel: string
    zipCode: string
    address: string
    businessHour: string
    holiday: string
    licenseNumber: string
    imageName: string
    cityName: string
    prefName: string
    area: {
        prefectures: Pref[],
        cities: City[]
    }
    daysOff: number[]
    workTimes: {
        sunday: { open: string | null, close: string | null }
        monday: { open: string | null, close: string | null }
        tuesday: { open: string | null, close: string | null }
        wednesday: { open: string | null, close: string | null }
        thursday: { open: string | null, close: string | null }
        friday: { open: string | null, close: string | null }
        saturday: { open: string | null, close: string | null }
        latitude: number
        longitude: number
        address: string
    }
}

export interface GetFcShopHouseDoListForm {
    prefId: number
}

export interface FcShopHouseDo {
    fcShopId: number
}

export interface GetFcShopTempoDetailForm {
    id: number
}

export interface WorkTime {
    open: `${number}:${number}:${number}` | null,
    close: `${number}:${number}:${number}` | null,
}

export interface FcShopTempoDetail {
    id: number,
    encodedShopId?: string,
    name: string,
    url: string,
    fcName: string,
    freeDial: string | null,
    tel: string,
    zipCode: string,
    address: string,
    businessHour: string,
    holiday: string,
    licenseNumber: string,
    imageName: string[],
    fax: string,
    email: string,
    accessText: {
      access1: string;
      access2: string;
      access3: string;
      etc: string;
    },
    parkingQuantity: string,
    fcManagerName: string,
    cityName: string,
    prefName: string,
    area: {
        prefectures: Pref[],
        cities: City[]
    }
    daysOff: number[],
    joinGroup: string[]
    kasika: { pid: string, cug: string },
    workTimes: {
        sunday: WorkTime,
        monday: WorkTime,
        tuesday: WorkTime,
        wednesday: WorkTime,
        thursday: WorkTime,
        friday: WorkTime,
        saturday: WorkTime,
    },
    latitude: number,
    longitude: number,
    gtmTag: string | null,
    personalGtmTag: string | null,
}

export interface GetConfigForm {
    fcShopId: number[],
}

interface ListFcShopTempoDetailQuery {
  id?: number | number[] | null;
}

type ShopStatisticResponseBody = {
  id: number;
  encodedShopId?: string;
  fullAddress: string;
  franchiseId: number;
  name: string;
  email: string | null;
  url: string | null;
  estatesToSale: {
    total: number,
    house: { new: number, used: number, },
    apartment: { new: number, used: number, }
    business: number,
    land: number,
  };
}[];

export default {

    async listFcShopTempoDetail(form: ListFcShopTempoDetailQuery): Promise<FcShopTempoDetail[]> {
      const response = await axios.get('/shop/', {
        params: form,
      });

      const data = response.data as {
        fcShopId: string,
        encodedShopId?: string,
        fcShopName: string,
        fcShopUrl: string,
        fcName: string,
        fcShopFreeDial: string | null,
        fcShopTel: string,
        fcShopZipCode: string,
        fcShopAddress: string,
        fcShopBusinessHour: string,
        fcShopHoliday: string,
        fcShopLicenseNumber: string,
        fcShopImageName: string,
        fcShopImageName2: string,
        fcShopImageName3: string,
        fcShopFax: string,
        fcShopMail: string,
        cityName: string,
        prefName: string,
        daysOff: number[],
        gtmTag: string | null,
        shopGtmTag: string | null,
        area: {
          prefectures: Pref[],
          cities: City[]
        },
        accessText: {
          access1: string;
          access2: string;
          access3: string;
          etc: string;
        },
        parkingQuantity: string,
        fcManagerName: string,
        joinGroup: string[],
        kasika: {
          pid: string,
          cug: string
        },
        workTimes: {
          sunday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          monday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          tuesday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          wednesday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          thursday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          friday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          saturday: {
            open: `${number}:${number}:${number}` | null,
            close: `${number}:${number}:${number}` | null,
          },
          latitude: string,
          longitude: string,
          address: string
        }
      }[];

      return data.map(shop => ({
        id: Number(shop.fcShopId),
        encodedShopId: shop.encodedShopId,
        name: shop.fcShopName,
        url: shop.fcShopUrl,
        fcName: shop.fcName,
        freeDial: shop.fcShopFreeDial,
        tel: shop.fcShopTel,
        zipCode: shop.fcShopZipCode,
        address: shop.fcShopAddress,
        businessHour: shop.fcShopBusinessHour,
        holiday: shop.fcShopHoliday,
        licenseNumber: shop.fcShopLicenseNumber,
        imageName: [shop.fcShopImageName, shop.fcShopImageName2, shop.fcShopImageName3].filter((i) => !!i),
        fax: shop.fcShopFax,
        email: shop.fcShopMail,
        accessText: shop.accessText,
        parkingQuantity: shop.parkingQuantity,
        fcManagerName: shop.fcManagerName,
        joinGroup: Array.isArray(shop.joinGroup) ? shop.joinGroup.filter((i) => !!i) : [],
        cityName: shop.cityName,
        prefName: shop.prefName,
        area: shop.area,
        daysOff: shop.daysOff,
        kasika: { pid: shop.kasika?.pid, cug: shop.kasika?.cug },
        workTimes: {
          sunday: { open: shop.workTimes?.sunday.open, close: shop.workTimes?.sunday.close },
          monday: { open: shop.workTimes?.monday.open, close: shop.workTimes?.monday.close },
          tuesday: { open: shop.workTimes?.tuesday.open, close: shop.workTimes?.tuesday.close },
          wednesday: { open: shop.workTimes?.wednesday.open, close: shop.workTimes?.wednesday.close },
          thursday: { open: shop.workTimes?.thursday.open, close: shop.workTimes?.thursday.close },
          friday: { open: shop.workTimes?.friday.open, close: shop.workTimes?.friday.close },
          saturday: { open: shop.workTimes?.saturday.open, close: shop.workTimes?.saturday.close },
        },
        latitude: shop.workTimes?.latitude ? Number.parseFloat(shop.workTimes.latitude) : 0,
        longitude: shop.workTimes?.longitude ? Number.parseFloat(shop.workTimes.longitude) : 0,
        gtmTag: shop.gtmTag,
        personalGtmTag: shop.shopGtmTag,
      }));
    },


  async getDetail(form: GetFcShopHouseDoDetailForm): Promise<FcShopHouseDoDetail> {
        const response = await axios.get('/tempo/getFcShopTempoDetail', {
            params: {fcShopId: form.fcShopId},
        });
        const data = response.data as {
            fcShopId: string,
            fcShopName: string,
            fcShopUrl: string,
            fcName: string,
            fcShopFreeDial: string,
            fcShopTel: string,
            fcShopZipCode: string,
            fcShopAddress: string,
            fcShopBusinessHour: string,
            fcShopHoliday: string,
            fcShopLicenseNumber: string,
            fcShopImageName: string,
            cityName: string,
            prefName: string,
            area: {
                prefectures: Pref[],
                cities: City[]
            }
            daysOff: number[],
            workTimes: {
                sunday: { open: string | null, close: string | null },
                monday: { open: string | null, close: string | null },
                tuesday: { open: string | null, close: string | null },
                wednesday: { open: string | null, close: string | null },
                thursday: { open: string | null, close: string | null },
                friday: { open: string | null, close: string | null },
                saturday: { open: string | null, close: string | null },
                latitude: string,
                longitude: string,
                address: string
            }
        };
        return {
            id: Number.parseInt(data.fcShopId, 10),
            shopName: data.fcShopName,
            url: data.fcShopUrl,
            name: data.fcName,
            freeDial: data.fcShopFreeDial,
            tel: data.fcShopTel,
            zipCode: data.fcShopZipCode,
            address: data.fcShopAddress,
            businessHour: data.fcShopBusinessHour,
            holiday: data.fcShopHoliday,
            licenseNumber: data.fcShopLicenseNumber,
            imageName: data.fcShopImageName,
            cityName: data.cityName,
            prefName: data.prefName,
            area: data.area,
            daysOff: data.daysOff,
            workTimes: {
                sunday: {open: data.workTimes.sunday.open, close: data.workTimes.sunday.close},
                monday: {open: data.workTimes.monday.open, close: data.workTimes.monday.close},
                tuesday: {open: data.workTimes.tuesday.open, close: data.workTimes.tuesday.close},
                wednesday: {open: data.workTimes.wednesday.open, close: data.workTimes.wednesday.close},
                thursday: {open: data.workTimes.thursday.open, close: data.workTimes.thursday.close},
                friday: {open: data.workTimes.friday.open, close: data.workTimes.friday.close},
                saturday: {open: data.workTimes.saturday.open, close: data.workTimes.saturday.close},
                latitude: Number.parseFloat(data.workTimes.latitude),
                longitude: Number.parseFloat(data.workTimes.longitude),
                address: data.workTimes.address,
            },
        };
    },

    async countEstateTypes(form: GetEstateCountForm): Promise<EstateCount[]> {
      const res = await axios.get<ShopStatisticResponseBody>('/shop/statistics', {
        params: {
          shopIds: form.shopIds,
          prefId: form.prefId ? String(form.prefId).padStart(2, '0'): undefined,
          cityId1: form.cityIds?.[0] ? Number(form.cityIds[0]) : undefined,
          cityId2: form.cityIds?.[1] ? Number(form.cityIds[1]) : undefined,
          cityId3: form.cityIds?.[2] ? Number(form.cityIds[2]) : undefined,
        },
      });
      return res.data.map((item) => ({
        shopId: item.id,
        encodedShopId: item.encodedShopId,
        fullAddress: item.fullAddress,
        total: item.estatesToSale.total,
        prefId: 0,
        cityId: 0,
        landCount: item.estatesToSale.land,
        newEstateCount: item.estatesToSale.house.new,
        newMansionCount: item.estatesToSale.apartment.new,
        oldEstateCount: item.estatesToSale.house.used,
        oldMansionCount: item.estatesToSale.apartment.used,
        businessCount: item.estatesToSale.business,
        shopName: item.name,
        prefName: '',
        address: '',
        cityName: '',
      }));
    },

    async getFcShopTempoDetail(form: GetFcShopTempoDetailForm): Promise<FcShopTempoDetail> {
        const res = await axios.get(`/shop/tempo/${form.id}`);
        const data = res.data as {
            fcShopId: string,
            encodedShopId?: string,
            fcShopName: string,
            fcShopUrl: string,
            fcName: string,
            fcShopFreeDial: string | null,
            fcShopTel: string,
            fcShopZipCode: string,
            fcShopAddress: string,
            fcShopBusinessHour: string,
            fcShopHoliday: string,
            fcShopLicenseNumber: string,
            fcShopImageName: string,
            fcShopImageName2: string,
            fcShopImageName3: string,
            fcShopFax: string,
            fcShopMail: string,
            cityName: string,
            prefName: string,
            daysOff: number[],
            gtmTag: string | null,
            shopGtmTag: string | null,
            area: {
                prefectures: Pref[],
                cities: City[]
            },
            accessText: {
              access1: string;
              access2: string;
              access3: string;
              etc: string;
            },
            parkingQuantity: string,
            fcManagerName: string,
            joinGroup: string[],
            kasika: {
                pid: string,
                cug: string
            }
            workTimes: {
                sunday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                monday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                tuesday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                wednesday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                thursday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                friday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                saturday: {
                    open: `${number}:${number}:${number}` | null,
                    close: `${number}:${number}:${number}` | null,
                },
                latitude: string,
                longitude: string,
                address: string
            }
        };
        return {
            id: Number(data.fcShopId),
            encodedShopId: data.encodedShopId,
            name: data.fcShopName,
            url: data.fcShopUrl,
            fcName: data.fcName,
            freeDial: data.fcShopFreeDial,
            tel: data.fcShopTel,
            zipCode: data.fcShopZipCode,
            address: data.fcShopAddress,
            businessHour: data.fcShopBusinessHour,
            holiday: data.fcShopHoliday,
            licenseNumber: data.fcShopLicenseNumber,
            imageName: [data.fcShopImageName, data.fcShopImageName2, data.fcShopImageName3].filter((i) => !!i),
            fax: data.fcShopFax,
            email: data.fcShopMail,
            accessText: data.accessText,
            parkingQuantity: data.parkingQuantity,
            fcManagerName: data.fcManagerName,
            joinGroup: Array.isArray(data.joinGroup) ? data.joinGroup.filter((i) => !!i) : [],
            cityName: data.cityName,
            prefName: data.prefName,
            area: data.area,
            daysOff: data.daysOff,
            kasika: {pid: data.kasika?.pid, cug: data.kasika?.cug},
            workTimes: {
                sunday: {open: data.workTimes?.sunday.open, close: data.workTimes?.sunday.close },
                monday: {open: data.workTimes?.monday.open, close: data.workTimes?.monday.close },
                tuesday: {open: data.workTimes?.tuesday.open, close: data.workTimes?.tuesday.close },
                wednesday: {open: data.workTimes?.wednesday.open, close: data.workTimes?.wednesday.close },
                thursday: {open: data.workTimes?.thursday.open, close: data.workTimes?.thursday.close },
                friday: {open: data.workTimes?.friday.open, close: data.workTimes?.friday.close },
                saturday: {open: data.workTimes?.saturday.open, close: data.workTimes?.saturday.close },
            },
            latitude: data.workTimes?.latitude ? Number.parseFloat(data.workTimes?.latitude) : 0,
            longitude: data.workTimes?.longitude ? Number.parseFloat(data.workTimes?.longitude) : 0,
            gtmTag: data.gtmTag,
            personalGtmTag: data.shopGtmTag,
        };
    },
};
